import React from "react";
import { RouteComponentProps } from "react-router";
import { useForm } from "react-hook-form";

import {
  EmptyState,
  Link,
  ScreenContainer,
  ScreenHeader,
  Button,
} from "../components/wombat-ui";

import { ReviewIcon } from "../components/icons";

import useTranslations from "../hooks/useTranslations";
import useNotification from "../hooks/useNotification";

import ReviewConfigurationForm from "../components/reviews/StepForm/ReviewConfigurationForm";
import { formatISO } from "date-fns/esm";
import { InsertReviewVariables, ReviewFullFragment, useGetReviewFull, useInsertReview } from '../graphql/generated/definitions';
import { getDefaultValues } from "@apollo/client/utilities";

type MissionFormVariables = Omit<
  InsertReviewVariables["input"],
  "collaboratorId"
> & { collaboratorIds: string[] };

type EditReviewScreenFormProps = { review: ReviewFullFragment };

const EditReviewScreenForm: React.FC<EditReviewScreenFormProps> = ({ review }) => {
  const t = useTranslations();

  const { notifySuccess, notifyError } = useNotification();

  const {
    register,
    handleSubmit,
    reset,
    ...useFormMethods
  } = useForm<MissionFormVariables>({
    defaultValues: {
      reviewSupervisorsAttributes: review.reviewSupervisors.map(
        ({ user, emailNotificationsEnabled }) => ({
          userId: user.id,
          emailNotificationsEnabled,
        })
      ),
      ...review,
      reminderEnabled: review.reminderEnabled ? "true" : "false"
    },
  });

  const [insertReview] = useInsertReview();

  const onSubmit = async ({ startDate, endDate, reminderEnabled, ...input }) => {
    try {
      await insertReview({
        variables: {
          input: {
            ...input,
            reminderEnabled: reminderEnabled === "true",
            startDate: formatISO(startDate),
            endDate: endDate ? formatISO(endDate) : null,
          }
        }
      })
      notifySuccess({
        title: t("userProfile.notification.success") as string,
      });
    } catch {
      notifyError({
        title: t("userProfile.notification.error") as string,
      });
    }
  };

  return (
    <form id="editReviewForm" onSubmit={handleSubmit(onSubmit)}>
      <ScreenHeader
        icon={ReviewIcon}
        title={t("reviews.updateReview", {
          title: review.template?.title,
        })}
        actions={[
          <Button key="submit" type="submit" variant="primary">
            {t("forms.actions.save")}
          </Button>,
        ]}
      />
      <input type="hidden" name="id" ref={register()} defaultValue={review.id} />
      <ReviewConfigurationForm
        defaultValues={review}
        {...{ register, reset, ...useFormMethods }}
      />
    </form>
  );
};

type EditReviewScreenProps = RouteComponentProps<{ id: string }>;

const EditReviewScreen: React.FC<EditReviewScreenProps> = ({ match }) => {
  const t = useTranslations();

  const { data, loading } = useGetReviewFull({
    variables: { id: match.params.id },
  });

  const review = data?.review;

  return (
    <ScreenContainer
      loading={loading}
      error={
        !review?.canInsert ? (
          <EmptyState
            title={t("network.unauthorized") as string}
            action={
              <Link variant="button" to="/">
                {t("network.backToHome")}
              </Link>
            }
          />
        ) : undefined
      }
    >
      {review && <EditReviewScreenForm review={review} />}
    </ScreenContainer>
  );
};

export default EditReviewScreen;
